/**
 * Utility functions for converting between relational radius points (0-100) and pixel radius
 */

export interface MapDimensions {
  width: number;
  height: number;
}

/**
 * Convert relational radius (0-100) to pixel radius based on map dimensions
 * @param relationalRadius - Value between 0-100
 * @param mapDimensions - Map width and height in pixels
 * @returns Pixel radius for the Circle component
 */
export const convertRelationalToPixelRadius = (
  relationalRadius: number,
  mapDimensions: MapDimensions
): number => {
  // Clamp input to 0-100 range
  const clampedRadius = Math.max(0, Math.min(100, relationalRadius));

  // Use the smaller dimension as base reference for more reasonable scaling
  const mapBaseSize = Math.min(mapDimensions.width, mapDimensions.height);

  // Calculate diagonal for coverage reference
  const mapHalfDiagonal = Math.sqrt(
    mapDimensions.width * mapDimensions.width +
    mapDimensions.height * mapDimensions.height
  ) / 2; // Half diagonal to reach from center to corner

  // 0 = 5% of base size (minimum visible), 100 = 200% of diagonal (generous coverage beyond map)
  const minRadius = mapBaseSize * 0.05;
  const maxRadius = mapHalfDiagonal * 2; // Double the diagonal for 100%

  // Linear interpolation between min and max
  const pixelRadius = minRadius + (clampedRadius / 100) * (maxRadius - minRadius);

  return Math.round(pixelRadius);
};

/**
 * Convert pixel radius back to relational radius (0-100)
 * @param pixelRadius - Current pixel radius
 * @param mapDimensions - Map width and height in pixels
 * @returns Relational radius value (0-100)
 */
export const convertPixelToRelationalRadius = (
  pixelRadius: number,
  mapDimensions: MapDimensions
): number => {
  const mapBaseSize = Math.min(mapDimensions.width, mapDimensions.height);

  const mapHalfDiagonal = Math.sqrt(
    mapDimensions.width * mapDimensions.width +
    mapDimensions.height * mapDimensions.height
  ) / 2;

  const minRadius = mapBaseSize * 0.05;
  const maxRadius = mapHalfDiagonal * 2;

  // Clamp pixel radius to valid range
  const clampedPixelRadius = Math.max(minRadius, Math.min(maxRadius, pixelRadius));

  // Convert back to 0-100 scale
  const relationalRadius = ((clampedPixelRadius - minRadius) / (maxRadius - minRadius)) * 100;

  return Math.round(Math.max(0, Math.min(100, relationalRadius)));
};

/**
 * Get a descriptive coverage percentage for display
 * @param relationalRadius - Value between 0-100
 * @returns Human-readable coverage description
 */
export const getRadiusCoverageDescription = (relationalRadius: number): string => {
  const clampedRadius = Math.max(0, Math.min(100, relationalRadius));

  if (clampedRadius === 0) return "Minimum (5% coverage)";
  if (clampedRadius === 100) return "Maximum (covers entire map area)";
  if (clampedRadius < 25) return `Small (${clampedRadius}% coverage)`;
  if (clampedRadius < 50) return `Medium (${clampedRadius}% coverage)`;
  if (clampedRadius < 75) return `Large (${clampedRadius}% coverage)`;
  return `Very Large (${clampedRadius}% coverage)`;
};